/**
 * Canonical path-changing redirects shared by middleware and revenue attribution.
 *
 * DISPOSITION FREEZE: this file is URL disposition config. The 2026-09-30
 * cleanup release applied every planned 301/410 at once; until the review date
 * 2026-12-20 a change here (or to crawl-policy-data.json, gone-paths.ts, the
 * middleware legacy rules or the sitemap generators) fails CI unless the commit
 * message or PR body carries `disposition-override: <reason>`. See
 * scripts/check_disposition_freeze.py.
 */
export const LEGACY_CRAWL_CANONICAL_PATHS: Readonly<Record<string, string>> = {
  "/free-astrology-chat/": "/ai-astrologer/",
  "/membership/": "/ai-astrologer/",
  "/matching/": "/kundli-matching/",
  // These pages already named these winners in their canonical metadata.
  // Resolve the same consolidation in routing and sitemap ownership.
  "/manglik-calculator/": "/mangal-dosha-calculator/",
  "/saturn-transit/": "/shani-transit/",
  // `vivah` was never a governed muhurat event id (the source-of-truth slug is
  // `marriage`) and the generic dynamic fallback earned 0 GSC rows/90d as of
  // 2026-08-04. Consolidate the orphan instead of grandfathering it into the
  // fail-closed dynamic-index registry.
  "/muhurat/vivah/": "/muhurat/marriage/",
  // Consolidated 2026-07-22: thin /prashna/ guide → the /prashna-kundali/ tool
  // (kundli-matching-style cannibalization; the tool page dominates the query).
  "/prashna/": "/prashna-kundali/",
  // `/vastu/` is a stale static artifact. The current hub, locale pair, room
  // directory, schema, and sitemap authority all live under `/vastu-shastra/`.
  "/vastu/": "/vastu-shastra/",
  "/kundali/": "/free-kundali/",
  "/daily-rashifal/": "/rashifal/",
  "/today-rashifal/": "/rashifal/",
  "/pages/about/": "/about/",
  "/pages/about-us/": "/about/",
  "/pages/contact/": "/contact/",
  "/pages/contact-us/": "/contact/",
  // Moved from the account-level Cloudflare Bulk Redirect list (deleted
  // 2026-10-01 so this map is the only redirect source). Same targets as live.
  "/pages/track-order/": "/contact/",
  "/wpm/": "/",
  "/policies/privacy-policy/": "/privacy/",
  "/policies/refund-policy/": "/refund-policy/",
  "/policies/shipping-policy/": "/shipping-policy/",
  "/policies/terms-of-service/": "/terms/",
  "/remedies/": "/upay/",
  "/hi/remedies/": "/hi/upay/",
  "/dosha/kaal-sarp-dosha/": "/kaal-sarp-dosha/",
  "/dosha/mangal-dosha/": "/mangal-dosha-calculator/",
  "/mangal-dosha/": "/mangal-dosha-calculator/",
  "/dosha/pitra-dosha/": "/pitra-dosha/",
  "/dosha/shani-dosha/": "/sade-sati/",
  "/dosha/rahu-dosha/": "/upay/rahu/",
  "/remedies/for-dosha/rahu_dosha/": "/upay/rahu/",
  "/dosha/ketu-dosha/": "/upay/ketu/",
  "/remedies/for-dosha/ketu_dosha/": "/upay/ketu/",
  "/hi/monthly-rashifal/": "/hi/aaj-ka-rashifal/",
  "/hi/muhurat/naamkaran/": "/hi/muhurat/",
  // Retired physical-product pages (Shopify catalog era, GSC 404 backlog 2026-07-24).
  // Only the karungali handles have an equivalent page. The other retired
  // products, the collections and the journal blog answer 410 since the
  // 2026-09-30 cleanup release (`shopify-leftover` in crawl-policy-data.json):
  // a 301 to the home page or /upay/ is a soft 404.
  //
  // FIXED 2026-08-09: all three pointed at
  // `/blogs/news/karungali-mala-benefits-...`, which does NOT exist and returns
  // 404. A live URL redirecting into a 404 is a soft-404 signal, and it wasted
  // whatever equity these paths still carried — on a domain under an active
  // algorithmic classification (ADR-0091) that is the opposite of what a redirect
  // is for. The page that actually survives is the editorial guide, verified 200.
  //
  // REPAIRED AGAIN 2026-08-09 (ADR-0092): `/products/karungali-mala/` is NO LONGER
  // a redirect — it is the live product route. The redirect was correct only while
  // the product had no page; keeping it after the pilot went live would 301 a
  // ready-to-buy visitor away from the thing they came to buy, into an editorial
  // post. The two legacy duplicate handles consolidate INTO that canonical product
  // page rather than the guide, and the combo handle is a VARIANT, which ADR-0092
  // says is never its own URL — so it resolves to the one product page that owns it.
  "/products/karungali-mala-1/": "/products/karungali-mala/",
  "/products/karungali-mala-siddh-rudraksha-bracelet-combo/": "/products/karungali-mala/",
  "/mahadasha/saturn/": "/mahadasha/shani/",
  "/blogs/news/baby-names-by-nakshatra-complete-guide-for-2026/": "/baby-names/",
  "/blogs/news/sade-sati-2026-which-rashis-are-affected-and-what-to-do/": "/sade-sati/",
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-t/": "/free-kundali/",
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-to-everyone/":
    "/free-kundali/",
  "/blogs/news/saturn-transit-in-aries-2026-effects-on-all-12-rashis/": "/shani-transit/",
  "/blogs/news/how-to-find-your-moon-sign-rashi-by-date-of-birth/": "/rashi-calculator/",
  "/blogs/news/kaal-sarp-dosha-signs-effects-and-proven-remedies/": "/kaal-sarp-dosha/",
  "/blogs/news/kundali-matching-beyond-36-gun-milan-what-really-matters/": "/kundli-matching/",
  "/blogs/news/kundli-matching-for-marriage-gun-milan-explained/": "/kundli-matching/",
  "/blogs/news/mangal-dosha-meaning-effects-and-remedies/": "/mangal-dosha-calculator/",
  "/blogs/news/what-is-kundli-complete-guide-to-vedic-birth-chart/": "/free-kundali/",
  "/blogs/news/nakshatra-the-27-lunar-mansions-of-vedic-astrology/": "/nakshatra/",
  "/pages/terms-and-conditions/": "/terms/",
  "/pages/privacy-policy/": "/privacy/",
  "/pages/delete-account/": "/privacy/",
  "/jupiter-transit-2026/": "/jupiter-transit/",
  "/rahu-transit-2026/": "/rahu-transit/",
  "/hi/jupiter-transit-2026/": "/hi/transits/",
  "/hi/rahu-transit-2026/": "/hi/transits/",
  "/kundali-milan/": "/kundli-matching/",
  "/kundli-milan/": "/kundli-matching/",
  "/hi/kundali-milan/": "/hi/kundli-matching/",
  "/hi/kundli-milan/": "/hi/kundli-matching/",
  "/lal-kitab-remedies/": "/lal-kitab/",
  // Explain retirement (owner decision 2026-09-28, cleanup release 2026-09-30):
  // an /explain/ page 301s only to a page carrying the same content; every other
  // /explain/ URL answers 410 (crawl-policy-data.json `explain*` gone rules). The
  // rashi, nakshatra and bhava explainers are pattern rules there.
  "/explain/dosha/guru-chandal-dosh/": "/guru-chandal-dosha/",
  "/explain/dosha/kaal-sarp-dosh/": "/kaal-sarp-dosha/",
  "/explain/dosha/kemadruma-yoga/": "/kemdrum-yoga/",
  "/explain/dosha/mangal-dosh/": "/mangal-dosha-calculator/",
  "/explain/dosha/pitra-dosh/": "/pitra-dosha/",
  "/explain/yoga/dhana-yoga/": "/dhana-yoga-calculator/",
  "/explain/yoga/kala-sarpa-yoga/": "/kaal-sarp-dosha/",
  "/explain/yoga/raja-yoga/": "/raj-yoga-calculator/",
  "/explain/koota/nadi/": "/nadi-dosha/",
  "/hi/explain/koota/nadi/": "/hi/nadi-dosh/",
  // The five rebuilt yoga guides (YOGA_GUIDES[].explainPath, pinned by
  // canonical-redirects.test.ts) replace their retired explainers one-to-one.
  "/explain/yoga/gajakesari-yoga/": "/yoga/gajakesari/",
  "/explain/yoga/budha-aditya-yoga/": "/yoga/budhaditya/",
  "/explain/yoga/malavya-yoga/": "/yoga/malavya/",
  "/explain/yoga/vipreet-raja-yoga/": "/yoga/viparita-raja-yoga/",
  "/explain/yoga/neecha-bhanga-raja-yoga/": "/yoga/neecha-bhanga-raja-yoga/",
  // One URL per paid report (cleanup release 2026-09-30): each report-intent
  // landing sold the same product as its /reports/<plan>/ page under a second
  // self-canonical URL (five pairs shared a title). The landing 301s to the
  // product page its own `reportSlug` names (pinned by canonical-redirects.test.ts).
  "/annual-forecast/": "/reports/annual-forecast/",
  "/baby-name-report/": "/reports/baby-name/",
  "/career-report/": "/reports/career-report/",
  "/career-astrology-report/": "/reports/career-report/",
  "/dosha-report/": "/reports/dosha-remedy-plan/",
  "/dosha-remedy-plan/": "/reports/dosha-remedy-plan/",
  "/gemstone-report/": "/reports/gemstone-recommendation/",
  "/gochar-report/": "/reports/gochar-transit/",
  "/lal-kitab-report/": "/reports/lal-kitab/",
  "/mahadasha-forecast/": "/reports/mahadasha-forecast/",
  "/marriage-report/": "/reports/marriage-report/",
  "/muhurat-report/": "/reports/muhurat-report/",
  "/numerology-report/": "/reports/numerology-life/",
  "/pitra-dosh-report/": "/reports/pitra-dosh/",
  "/sade-sati-report/": "/reports/sade-sati/",
  // The two Hindi dosha landings sold the same Dosha Remedy Plan; the one whose
  // slug matches the product keeps the Hindi URL.
  "/hi/dosha-report/": "/hi/dosha-remedy-plan/",
  // Keyword-variant landings of the one AI-chat product (5-gram Jaccard 0.36,
  // 0 clicks in the 28 days before the release). Their Hindi pages stay and
  // declare /ai-astrologer/ as their English alternate (src/i18n/locales.ts).
  "/chat-with-astrologer/": "/ai-astrologer/",
  "/talk-to-astrologer/": "/ai-astrologer/",
  // Wave 2 merged each ekadashi guide into its festival page; the pairs are
  // EKADASHI_GUIDE_REDIRECTS (src/lib/ekadashi-data.ts), pinned by the test.
  "/ekadashi/nirjala-ekadashi/": "/calendar/festival/nirjala_ekadashi/",
  "/ekadashi/devshayani-ekadashi/": "/calendar/festival/devshayani_ekadashi/",
  "/ekadashi/devuthani-ekadashi/": "/calendar/festival/devuthani_ekadashi/",
  "/ekadashi/mokshada-ekadashi/": "/calendar/festival/mokshada_ekadashi/",
  "/ekadashi/papankusha-ekadashi/": "/calendar/festival/papankusha_ekadashi/",
  "/ekadashi/amalaki-ekadashi/": "/calendar/festival/amalaki_ekadashi/",
  "/ekadashi/varuthini-ekadashi/": "/calendar/festival/varuthini_ekadashi/",
  "/ekadashi/kamada-ekadashi/": "/calendar/festival/kamada_ekadashi/",
  // One sitemap entry point and one feed (cleanup release 2026-09-30). The
  // content Worker applies this map before serving any sitemap route.
  "/sitemap.xml": "/sitemap-index.xml",
  "/sitemap-content.xml": "/sitemap-index.xml",
  "/jyotish-sitemap.xml": "/sitemap-index.xml",
  "/rss.xml": "/feed.xml",
  // A year-suffixed festival id whose evergreen twin carries every year's date.
  "/calendar/festival/makar_sankranti_2027/": "/calendar/festival/makar_sankranti/",
  "/hi/calendar/festival/makar_sankranti_2027/": "/hi/calendar/festival/makar_sankranti/",
};
